import { useState, useEffect, useRef } from "react";
import { Responsive } from "react-grid-layout";
import type { Layout } from "react-grid-layout";
import { Button } from "@/shared/ui/button";
import { LayoutGrid, Lock, Unlock, Settings } from "lucide-react";
import { useFactory } from "@/core/hooks/useFactory";
import { ProductionKPIWidget } from "@/core/components/factory-dashboard/ProductionKPIWidget";
import { QualityOverviewWidget } from "@/core/components/factory-dashboard/QualityOverviewWidget";
import { ProductionTrendWidget } from "@/core/components/factory-dashboard/ProductionTrendWidget";
import { ResourceUtilizationWidget } from "@/core/components/factory-dashboard/ResourceUtilizationWidget";
import { AlertsWidget } from "@/core/components/factory-dashboard/AlertsWidget";
import { ProductionByLineWidget } from "@/core/components/factory-dashboard/ProductionByLineWidget";
import { DowntimeAnalysisWidget } from "@/core/components/factory-dashboard/DowntimeAnalysisWidget";
import { ShiftPerformanceWidget } from "@/core/components/factory-dashboard/ShiftPerformanceWidget";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const ResponsiveGridLayout = Responsive;

const DEFAULT_LAYOUTS = {
  lg: [
    { i: "production-kpi", x: 0, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
    { i: "production-trend", x: 3, y: 0, w: 5, h: 3, minW: 3, minH: 2 },
    { i: "alerts", x: 8, y: 0, w: 4, h: 4, minW: 3, minH: 3 },
    { i: "quality-overview", x: 0, y: 2, w: 3, h: 3, minW: 2, minH: 3 },
    { i: "production-by-line", x: 3, y: 3, w: 5, h: 3, minW: 3, minH: 3 },
    { i: "resource-utilization", x: 0, y: 5, w: 3, h: 3, minW: 2, minH: 3 },
    { i: "downtime-analysis", x: 3, y: 6, w: 5, h: 3, minW: 3, minH: 3 },
    { i: "shift-performance", x: 8, y: 4, w: 4, h: 4, minW: 3, minH: 3 },
  ],
  md: [
    { i: "production-kpi", x: 0, y: 0, w: 4, h: 2, minW: 2, minH: 2 },
    { i: "production-trend", x: 4, y: 0, w: 6, h: 3, minW: 3, minH: 2 },
    { i: "alerts", x: 0, y: 2, w: 4, h: 4, minW: 3, minH: 3 },
    { i: "quality-overview", x: 4, y: 3, w: 6, h: 3, minW: 2, minH: 3 },
    { i: "production-by-line", x: 0, y: 6, w: 5, h: 3, minW: 3, minH: 3 },
    { i: "resource-utilization", x: 5, y: 6, w: 5, h: 3, minW: 2, minH: 3 },
    { i: "downtime-analysis", x: 0, y: 9, w: 5, h: 3, minW: 3, minH: 3 },
    { i: "shift-performance", x: 5, y: 9, w: 5, h: 4, minW: 3, minH: 3 },
  ],
  sm: [
    { i: "production-kpi", x: 0, y: 0, w: 6, h: 2, minW: 2, minH: 2 },
    { i: "production-trend", x: 0, y: 2, w: 6, h: 3, minW: 3, minH: 2 },
    { i: "alerts", x: 0, y: 5, w: 6, h: 4, minW: 3, minH: 3 },
    { i: "quality-overview", x: 0, y: 9, w: 6, h: 3, minW: 2, minH: 3 },
    { i: "production-by-line", x: 0, y: 12, w: 6, h: 3, minW: 3, minH: 3 },
    { i: "resource-utilization", x: 0, y: 15, w: 6, h: 3, minW: 2, minH: 3 },
    { i: "downtime-analysis", x: 0, y: 18, w: 6, h: 3, minW: 3, minH: 3 },
    { i: "shift-performance", x: 0, y: 21, w: 6, h: 4, minW: 3, minH: 3 },
  ],
};

export default function FactoryDashboard() {
  const { selectedFactory } = useFactory();
  const [layouts, setLayouts] = useState<any>(DEFAULT_LAYOUTS);
  const [isLocked, setIsLocked] = useState(true);
  const [containerWidth, setContainerWidth] = useState(1200);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track container width for responsive behavior
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Load saved layouts from localStorage
  useEffect(() => {
    if (selectedFactory?.id) {
      const saved = localStorage.getItem(`factory-dashboard-layout-${selectedFactory.id}`);
      if (saved) {
        try {
          setLayouts(JSON.parse(saved));
        } catch (e) {
          console.error("Failed to load dashboard layout:", e);
        }
      }
    }
  }, [selectedFactory?.id]);

  // Save layouts to localStorage
  const handleLayoutChange = (currentLayout: Layout[], allLayouts: any) => {
    if (!isLocked && selectedFactory?.id) {
      setLayouts(allLayouts);
      localStorage.setItem(`factory-dashboard-layout-${selectedFactory.id}`, JSON.stringify(allLayouts));
    }
  };

  const resetLayout = () => {
    setLayouts(DEFAULT_LAYOUTS);
    if (selectedFactory?.id) {
      localStorage.removeItem(`factory-dashboard-layout-${selectedFactory.id}`);
    }
  };

  const widgets = [
    { id: "production-kpi", component: <ProductionKPIWidget /> },
    { id: "production-trend", component: <ProductionTrendWidget /> },
    { id: "alerts", component: <AlertsWidget /> },
    { id: "quality-overview", component: <QualityOverviewWidget /> },
    { id: "production-by-line", component: <ProductionByLineWidget /> },
    { id: "resource-utilization", component: <ResourceUtilizationWidget /> },
    { id: "downtime-analysis", component: <DowntimeAnalysisWidget /> },
    { id: "shift-performance", component: <ShiftPerformanceWidget /> },
  ];

  // Add static property to layouts when locked
  const getLayoutsWithStatic = () => {
    if (isLocked) {
      const lockedLayouts: any = {};
      Object.keys(layouts).forEach((breakpoint) => {
        lockedLayouts[breakpoint] = layouts[breakpoint].map((item: any) => ({
          ...item,
          static: true,
        }));
      });
      return lockedLayouts;
    }
    return layouts;
  };

  return (
    <div className="min-h-screen p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <LayoutGrid className="h-7 w-7" />
            Factory Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Real-time overview of {selectedFactory?.name || "your factory"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsLocked(!isLocked)}
            className="gap-2"
          >
            {isLocked ? (
              <>
                <Lock className="h-4 w-4" />
                Locked
              </>
            ) : (
              <>
                <Unlock className="h-4 w-4" />
                Unlocked
              </>
            )}
          </Button>
          {!isLocked && (
            <Button variant="outline" size="sm" onClick={resetLayout} className="gap-2">
              <Settings className="h-4 w-4" />
              Reset Layout
            </Button>
          )}
        </div>
      </div>

      {/* Dashboard Grid */}
      <div ref={containerRef} className={`${!isLocked ? "dashboard-editing" : ""}`}>
        <ResponsiveGridLayout
          className="layout"
          layouts={getLayoutsWithStatic()}
          breakpoints={{ lg: 1200, md: 996, sm: 768 }}
          cols={{ lg: 12, md: 10, sm: 6 }}
          rowHeight={80}
          width={containerWidth}
          isDraggable={!isLocked}
          isResizable={!isLocked}
          isDroppable={!isLocked}
          onLayoutChange={handleLayoutChange}
          draggableHandle=".drag-handle"
          compactType="vertical"
          preventCollision={false}
          useCSSTransforms={true}
        >
          {widgets.map((widget) => (
            <div key={widget.id} className="relative">
              {!isLocked && (
                <div className="drag-handle absolute top-2 left-2 right-2 h-6 bg-primary/10 rounded cursor-move flex items-center justify-center z-10">
                  <div className="w-8 h-1 bg-primary/30 rounded-full" />
                </div>
              )}
              <div className={`h-full ${!isLocked ? "pt-8" : ""}`}>
                {widget.component}
              </div>
            </div>
          ))}
        </ResponsiveGridLayout>
      </div>

      {/* Info banner when unlocked */}
      {!isLocked && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <LayoutGrid className="h-4 w-4" />
          <span className="text-sm font-medium">
            Drag widgets to rearrange, resize by dragging corners. Click "Locked" to save.
          </span>
        </div>
      )}

      <style>{`
        .react-grid-item {
          transition: all 200ms ease;
          transition-property: left, top, width, height;
        }
        .react-grid-item.react-grid-placeholder {
          background: oklch(from var(--primary) l c h / 0.2);
          opacity: 0.5;
          transition-duration: 100ms;
          z-index: 2;
          border-radius: 8px;
          border: 2px dashed var(--primary);
        }
        .react-grid-item.resizing {
          transition: none;
          z-index: 100;
          will-change: width, height;
        }
        .react-grid-item.react-draggable-dragging {
          transition: none;
          z-index: 100;
          will-change: transform;
        }
        .react-grid-item > .react-resizable-handle {
          position: absolute;
          width: 20px;
          height: 20px;
          display: none;
        }
        .dashboard-editing .react-grid-item > .react-resizable-handle {
          display: block;
        }
        .react-grid-item > .react-resizable-handle::after {
          content: "";
          position: absolute;
          right: 3px;
          bottom: 3px;
          width: 8px;
          height: 8px;
          border-right: 2px solid oklch(from var(--primary) l c h / 0.5);
          border-bottom: 2px solid oklch(from var(--primary) l c h / 0.5);
        }
        .react-grid-item.static {
          cursor: default !important;
        }
        .react-grid-item.static > .react-resizable-handle {
          display: none !important;
        }
        .dashboard-editing .react-grid-item {
          border: 2px dashed transparent;
          border-radius: 8px;
          transition: border-color 200ms;
        }
        .dashboard-editing .react-grid-item:hover {
          border-color: oklch(from var(--primary) l c h / 0.3);
        }
      `}</style>
    </div>
  );
}
